import React, { useMemo, useState, useRef, useEffect } from "react";
import SEO from "../components/seo";
import { Flex, Heading, Box, Link as RebassLink } from "rebass";
import { Map, TileLayer, ZoomControl } from 'react-leaflet';
import Marker from 'react-leaflet-enhanced-marker';
import { path } from 'ramda';
import Bar from "../components/bar";
import { graphql } from "gatsby";
import Blur from "../components/blur";
import { useColorMode } from "theme-ui";
import Fullpage from "../components/fullpage";
import Webform from '../modules/webform';
import Modal from "../components/modal";
import FirstSlide from "../components/firstSlide";
import GoogleMapReact from 'google-map-react';
import Markup from "../components/markup";
import LogoFull from "../components/logoFull";
import { FaFacebookSquare, FaLinkedinIn, FaTwitter, FaVimeo, FaRss } from 'react-icons/fa';
import Logo from "../components/logo";
import ModalLink from "../components/modalLink";
import { getFieldsByName, useGetFieldsByName } from "../utils";
import useFirstPose from "../hooks/useFirstPose";
import BackgroundSlide from "../components/backgroundSlide";
import Parallax from "../components/parallax";
import BlurredBlocks from "../components/blurredBlocks";
import 'leaflet/dist/leaflet.css';

const icons = {
	fb: FaFacebookSquare,
	ln: FaLinkedinIn,
	vi: FaVimeo,
	tw: FaTwitter,
	rss: FaRss,
};

const osmProvider = {
	maptiler: {
		url: `https://api.maptiler.com/maps/streets-v2/256/{z}/{x}/{y}.png?key=${process.env.GATSBY_MAP_KEY || 'm4NYW6qGLGebxC7qfmQx'}`,
		attribution: '<a href="https://www.maptiler.com/copyright/" target="_blank">&copy; MapTiler</a> <a href="https://www.openstreetmap.org/copyright" target="_blank">&copy; OpenStreetMap contributors</a>'
	},
};

const ContactsPage = ({ data }) => {
	const { contact, ogImage, parallax, parallaxLune, parallaxBg } = data;

	const {
		block: { nodes: blocks, socials },
		map,
		contacts,
		mentionLegalesLink,
		mentionLegalesContent,
		cguLink,
		cguContent
	} = contact;

	const [colorMode] = useColorMode();
	const [activeForm, setActiveForm] = useState(null);
	const [formSubmitted, setFormSubmitted] = useState(false);
	const fromItem = useMemo(() => blocks.find(block => block.id === activeForm), [activeForm, blocks]);
	const firstSlide = useGetFieldsByName(['title', 'subtitle', 'description'], contact, colorMode);

	const [mapContainerHeight, setMapContainerHeight] = useState(0);
  const mapContainerRef = useRef(null);
  const footerRef = useRef(null);

	let resizeObserver;

	useEffect(() => {
		if (mapContainerRef.current) {
			resizeObserver = new ResizeObserver(entries => {
				const newHeight = entries[0].contentRect.height;
				setMapContainerHeight(newHeight - footerRef.current?.clientHeight);
			});

			resizeObserver.observe(mapContainerRef.current);
		}

		return () => {
			if (resizeObserver && mapContainerRef.current) {
				resizeObserver.unobserve(mapContainerRef.current);
				resizeObserver.disconnect();
			}
		};
	}, [mapContainerRef.current]);

	const pose = useFirstPose();

	const mappedBlocks = useMemo(() => blocks.map((block) => {
		const {
			link,
			title,
			description
		} = getFieldsByName(['link', 'title', 'description'], block, colorMode);
		return ({
			link,
			title,
			description,
			id: block.id,
		})
	}), [colorMode, blocks]);

	return (
		<>
			<SEO title="Contact" image={ogImage}/>
			<Fullpage
				background={({ blurred }) => (
					<Blur active={blurred} darken={colorMode === 'dark'} lighten={colorMode === 'default'} grayscale={false}
								sx={{ height: '100%', width: '100%', position: 'absolute', top: 0, left: 0, willChange: 'filter' }}>
						<>
							<BackgroundSlide
								mixedBg={<Parallax layers={parallaxBg.layers} disabled={blurred || pose === 'hidden'}/>}
							/>
							<Parallax layers={parallaxLune.layers} disabled={blurred || pose === 'hidden'}/>
							<BackgroundSlide
								mixedBg={<Parallax layers={parallax.layers} disabled={blurred || pose === 'hidden'}/>}
							/>
						</>
					</Blur>
				)}
				sections={[
					() => <FirstSlide {...firstSlide} color='white' pose={pose}/>,
					() =>
						<>
							<BlurredBlocks
								onBlockClick={setActiveForm}
								blocks={mappedBlocks}
							/>
							<Modal
								isOpen={!!activeForm && fromItem && fromItem.relationships && fromItem.relationships.webform}
								handleClose={() => {
									setActiveForm(null);
									setFormSubmitted(false)
								}}
							>
								{fromItem && fromItem.relationships && fromItem.relationships.webform &&
								<Flex width={[null, null, 600]} minHeight={[null, null, 300]} py={3} alignItems="center"
											justifyContent="center">
									{!formSubmitted ? (
										<Webform
											debug
											webform={fromItem.relationships.webform}
											endpoint={`${process.env.GATSBY_DRUPAL_BASE_URL}/gatsby_webform/submit`}
											onSuccess={() => setFormSubmitted(true)}
										/>) : (
										<Flex height="100%" flexDirection="column" alignItems="center" justifyContent="center">
											<Heading fontSize={7}>
												Merci
											</Heading>
											<Bar>on vous recontactera ;)</Bar>
										</Flex>
									)}
								</Flex>
								}
							</Modal>
						</>,
					() => {
						return (
							<Flex flexDirection="column" height="100%" width="100%" ref={mapContainerRef}>
								<Box
									sx={{ flexGrow: 1 }}
									width="100%"
									height={['60vh', null, 'auto']}
								>
									{mapContainerHeight && typeof window !== 'undefined' && map && map.lat && map.lng ? (
										<Map
											zoom={15}
											zoomControl={false}
											scrollWheelZoom={false}
											center={{ lat: map.lat, lng: map.lng }}
											style={{ height: `${mapContainerHeight}px` }}
										>
											<TileLayer
												url={osmProvider.maptiler.url}
												attribution={osmProvider.maptiler.attribution}
											/>
											<Marker
												position={[map.lat, map.lng]}
												icon={<CustomMarker />}
											/>
											<ZoomControl position="bottomright" />
										</Map>

										// <GoogleMapReact
										// 	bootstrapURLKeys={{ key: process.env.GATSBY_GOOGLE_MAP_KEY }}
										// 	defaultCenter={[map.lat, map.lng]}
										// 	defaultZoom={15}
										// >
										// 	<Logo
										// 		hideGif
										// 		lat={map.lat}
										// 		lng={map.lng}
										// 		sx={{ position: 'relative', transform: '', left: 0, top: 0 }} onClick={null}
										// 	/>
										// </GoogleMapReact>
									) : null}
								</Box>
								<Flex sx={{ flexShrink: 0 }} bg="bg" justifyContent="center" ref={footerRef}>
									<Flex flexDirection={['column', null, 'row']} width="100%" maxWidth={[null, null, 800, 1100]}
												justifyContent={[null, null, 'space-between']} alignItems={['center']} px={[4, null, 0]}
												py={[5, null, null, 6]}>
										<LogoFull width={[250, 300, 230]} sx={{ order: [null, null, 1] }}/>
										<Flex mt={[5, null, 0]} sx={{ order: [null, null, 2] }}>
											{socials.map((network, i) => {
												return network && network.name && network.link && network.link.uri ? (
													<RebassLink
														key={path(['link', 'uri'], network)}
														href={path(['link', 'uri'], network)}
														bg="text"
														color="background"
														height={[25]}
														width={[25]}
														target="_blank"
														mr={[i + 1 === socials.length ? 0 : 3]}
														sx={{
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'center',
															borderRadius: '50%',
														}}
													>
														{network.name && React.createElement(icons[network.name], { size: 12 })}
													</RebassLink>
												) : null;
											})}
										</Flex>
										<Box mt={[3, null, 0]} sx={{ order: [null, null, 0] }}>
											<Markup html={path(['processed'], contacts)}
															sx={{ textAlign: ['center', null, 'left'], fontSize: [1] }}/>
											<Flex mt={[4]} justifyContent={['center', null, 'flex-start']}>
												<ModalLink text={mentionLegalesLink} content={mentionLegalesContent} mr={4}/>
												<ModalLink text={cguLink} content={cguContent}/>
											</Flex>
										</Box>
									</Flex>
								</Flex>
							</Flex>
						)
					}
				]}
			/>
		</>
	)
};

const CustomMarker = () => {
	return (
		<Logo
			sx={{ position: 'relative', transform: '', left: 0, top: 0, '& img': { filter: 'none' } }}
			onClick={null}
			colorMode="dark"
		/>
	);
};

export default ContactsPage;

export const query = graphql`
    query ContactsQuery
    {
        contact: nodeContact
        {
            subtitle: field_sub_title
            title
            description: field_description_brut
            description_dark: field_dark_description_brut
            subtitle_dark: field_dark_sub_title
            title_dark: field_dark_titre
            id
            map: field_map
            {
                lat
                lng
            }
            mentionLegalesLink: field_mention_legales_link
            mentionLegalesContent: field_mention_legales_textg
            {
                processed
            }
            cguLink: field_cgu_link
            cguContent: field_cgu_te
            {
                processed
            }
            contacts: field_footer_text_contact
            {
                processed
            }
            block: relationships
            {
                nodes: field_webform_block
                {
                    id
                    title: field_title
                    link: field_link_title
                    description: field_description_brut
                    description_dark: field_dark_description_brut
                    link_dark: field_dark_link_title
                    title_dark: field_dark_titre
                    relationships
                    {
                        webform: field_webform
                        {
                            drupal_internal__id
                            elements
                            {
                                name
                                type
                                attributes
                                {
                                    name
                                    value
                                }
                                options
                                {
                                    label
                                    value
                                }
                            }
                        }
                    }
                }
                socials: field_social_networks
                {
                    link: field_link
                    {
                        uri
                        title
                    }
                    name: field_social_network
                }
            }
        }
        parallax: configsJson(name
        :
        {
            eq: "contacts-mixed"
        }
        )
        {
            layers
            {
                depth
                overflow
                image
                {
                    id
                    childImageSharp
                    {
                        original
                        {
                            width
                            height
                        }
                        fluid(maxWidth
                        :
                        3840, quality
                        :
                        100
                        )
                        {
                            ...
                            GatsbyImageSharpFluid_withWebp
                        }
                    }
                }
            }
        }
        parallaxBg: configsJson(name
        :
        {
            eq: "contacts-mixed-bg"
        }
        )
        {
            layers
            {
                depth
                overflow
                image
                {
                    id
                    childImageSharp
                    {
                        original
                        {
                            width
                            height
                        }
                        fluid(maxWidth
                        :
                        3840, quality
                        :
                        100
                        )
                        {
                            ...
                            GatsbyImageSharpFluid_withWebp
                        }
                    }
                }
            }
        }
        parallaxLune: configsJson(name
        :
        {
            eq: "contacts-lune"
        }
        )
        {
            layers
            {
                depth
                overflow
                image
                {
                    id
                    childImageSharp
                    {
                        original
                        {
                            width
                            height
                        }
                        fluid(maxWidth
                        :
                        1920, quality
                        :
                        100
                        )
                        {
                            ...
                            GatsbyImageSharpFluid_withWebp
                        }
                    }
                }
            }
        }
        ogImage: file(name
        :
        {
            eq: "og_contact"
        }
        )
        {
            ...
            ogImageFragment
        }
    }
`
